import { useEffect } from "react";

// Small SEO helpers for a CRA SPA. Google crawls rendered JS, so setting
// title/meta/JSON-LD via useEffect is picked up correctly.

const ensureMeta = (selector, attr, name, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const ensureLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

/**
 * useSeo({ title, description, canonical, image, type })
 * Sets <title>, meta description, canonical URL, and Open Graph / Twitter tags.
 */
export function useSeo({ title, description, canonical, image, type = "website" }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const desc = description || "";
    const url = canonical || (typeof window !== "undefined" ? window.location.href : "");
    const img = image || "";

    if (desc) ensureMeta('meta[name="description"]', "name", "description", desc);
    if (url) ensureLink("canonical", url);

    if (title) ensureMeta('meta[property="og:title"]', "property", "og:title", title);
    if (desc) ensureMeta('meta[property="og:description"]', "property", "og:description", desc);
    if (img) ensureMeta('meta[property="og:image"]', "property", "og:image", img);
    if (url) ensureMeta('meta[property="og:url"]', "property", "og:url", url);
    ensureMeta('meta[property="og:type"]', "property", "og:type", type);

    if (title) ensureMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    if (desc) ensureMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    if (img) ensureMeta('meta[name="twitter:image"]', "name", "twitter:image", img);
    ensureMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, canonical, image, type]);
}

/**
 * useJsonLd(nodes) — nodes is an array of JSON-LD objects. Each is injected
 * as its own <script type="application/ld+json"> in <head> and cleaned up on
 * unmount / dependency change.
 */
export function useJsonLd(nodes) {
  const key = JSON.stringify(nodes || []);
  useEffect(() => {
    if (!nodes || !nodes.length) return;
    const scripts = nodes.map((n) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoInjected = "1";
      s.text = JSON.stringify(n);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
